/*
  The service handles backend communication and initializing and continuing the session.
  This should only run once on the page.
 */

import { v4 as createUuid } from "uuid";

import {
  getSolvariSessionId,
  setCookie,
  singleton,
  sSessionStorage,
} from "@solvari/utils";

import { initRootClient } from "@/client/rootClient";
import {
  applyArgusUuidToUrl,
  argusSession,
  shouldApplyArgusSession,
} from "@/client/thinClient";

const crossDomainSession = singleton(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const referrerUrlParams = document.referrer
    ? new URLSearchParams(new URL(document.referrer).search)
    : null;

  const session = {
    sessionUuid:
      urlParams.get("solvariSession") ??
      referrerUrlParams?.get("solvariSession") ??
      null,
    clientId:
      urlParams.get("solvariClientId") ??
      referrerUrlParams?.get("solvariClientId") ??
      null,
  };

  urlParams.delete("solvariClientId");
  urlParams.delete("solvariSession");

  return session;
});

function initSessionId(sessionType: SessionType) {
  if (sessionType === "new") {
    // If no session is present there should not be any events
    sSessionStorage.removeItem("argusEvents");
    const uuid = createUuid();
    storeSolvariSessionUuid(uuid);
    return uuid;
  }

  if (sessionType === "cross-domain") {
    const uuid = crossDomainSession().sessionUuid!;
    storeSolvariSessionUuid(uuid);
    return uuid;
  }

  return getSolvariSessionId()!;
}

function initArgus() {
  const clientId =
    crossDomainSession().clientId ?? argusSession().clientId ?? createUuid();
  storeSolvariClientId(clientId);

  const sessionType = getSessionType();
  const sessionUuid = initSessionId(sessionType);
  initCrossDomainLinker();

  const rootClientPromise = initRootClient(sessionType).flush();

  return { sessionUuid, clientId, rootClientPromise };
}

type SessionType = "cross-domain" | "new" | "same-domain";
function getSessionType(): SessionType {
  if (crossDomainSession().sessionUuid) {
    return "cross-domain";
  }

  if (argusSession().sessionUuid) {
    return "same-domain";
  }

  return "new";
}

function storeSolvariClientId(uuid: string) {
  setCookie({ name: "solvariClientId", value: uuid, maxAge: "31536000" });
}

function storeSolvariSessionUuid(uuid: string) {
  sSessionStorage.setItem("solvariSession", uuid);
}

function initCrossDomainLinker() {
  document.addEventListener("click", (event) => {
    const anchorElement = (
      event.target as HTMLElement
    ).closest<HTMLAnchorElement>("a[href]");

    if (anchorElement) {
      anchorElement.href = applyArgusUuidToUrl(anchorElement.href);
    }
  });

  document.addEventListener("submit", (event) => {
    const formElement = (event.target as HTMLElement).closest<HTMLFormElement>(
      "form[action][method=get]",
    );

    if (formElement && shouldApplyArgusSession(formElement.action)) {
      const session = argusSession();
      formElement.insertAdjacentHTML(
        "beforeend",
        `<input type="hidden" name="solvariSession" value="${session.sessionUuid}"/>`,
      );
      formElement.insertAdjacentHTML(
        "beforeend",
        `<input type="hidden" name="solvariClientId" value="${session.clientId}"/>`,
      );
    }
  });
}

export {
  getSessionType,
  initArgus,
  storeSolvariClientId,
  storeSolvariSessionUuid,
};
export type { SessionType };
